import { useApolloClient } from "@apollo/client";
import { Select, TextWithBreaks } from "@msys/ui";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import { Grid, MenuItem, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { intersection } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  ConfirmModalProps,
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { ItemType, namedOperations } from "../../../../clients/graphqlTypes.js";
import { useItemTypes } from "../hooks/useItemTypes.js";
import { useItemTypeMenuItem_ChangeItemTypeMutationWithWizardRefetch } from "./ItemTypeMenuItem.mutations.js";
import { useItemTypeMenuItem_PossibleItemTypesQuery } from "./ItemTypeMenuItem.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  itemType: ItemType;
  allowedTypes?: ItemType[];
  expandedItemIds: string[] | undefined;
  disabled?: boolean;
  refetchWizardQuestions: () => Promise<unknown>;
}

export const ItemTypeMenuItem = ({
  projectId,
  docId,
  itemId,
  itemType,
  allowedTypes,
  expandedItemIds,
  disabled,
  refetchWizardQuestions,
}: Props) => {
  const { t } = useTranslate("QuoteItem");
  const { itemTypeOptions } = useItemTypes();

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const startConfirmProcess = React.useCallback((props: ConfirmModalProps) => {
    return confirmProcessRef.current!.startConfirmProcess(props);
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const query = useItemTypeMenuItem_PossibleItemTypesQuery({
    client,
    variables: {
      projectId,
      docId,
      itemId,
    },
    fetchPolicy: "no-cache",
  });

  const types = query.data?.itemPossibleItemTypes?.types ?? [];
  const warnings = query.data?.itemPossibleItemTypes?.warnings ?? [];
  const possibleTypes = allowedTypes
    ? intersection(types, allowedTypes)
    : types;

  const [changeItemType, { loading: changeItemTypeLoading }] =
    useItemTypeMenuItem_ChangeItemTypeMutationWithWizardRefetch({
      client,
      refetchWizardQuestions,
    });

  const modifyItemType = async (values: { type: ItemType }) => {
    if (warnings.includes(values.type)) {
      const isConfirmed = await startConfirmProcess({
        title: t("Attention"),
        content: () => (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <WarningAmberIcon color="warning" fontSize="large" />
            <Typography>
              <TextWithBreaks
                text={t(
                  "You are changing the item type.\nSome data may get lost."
                )}
              />
            </Typography>
          </Stack>
        ),
      });
      if (!isConfirmed) return;
    }

    try {
      await changeItemType({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            newType: values.type,
            expandedItemIds,
          },
        },
        refetchQueries: [
          namedOperations.Query.ItemTypeMenuItem_PossibleItemTypes,
        ],
        awaitRefetchQueries: true,
      });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  if (query.loading && !query.data) return null;

  return (
    <MenuItem
      disableRipple
      onClick={(event: React.MouseEvent<HTMLLIElement>) => {
        event.stopPropagation();
      }}
      disabled={disabled}
    >
      <Grid
        container
        sx={{
          alignItems: "center",
          minWidth: "310px",
        }}
      >
        <Grid size={5}>{t("Item type")}</Grid>
        <Grid
          size={7}
          sx={{
            minWidth: "180px",
          }}
        >
          <Select
            autoWidth
            fullWidth
            disabled={changeItemTypeLoading}
            value={itemType}
            label={t("Item type")}
            size="extra-small"
            onChange={async (value: ItemType) => {
              await modifyItemType({ type: value });
            }}
            options={itemTypeOptions.filter(
              o => o.value === itemType || possibleTypes.includes(o.value)
            )}
          />
        </Grid>
      </Grid>
      <ConfirmProcess ref={confirmProcessRef} />
    </MenuItem>
  );
};
